import React from 'react';
import {FormikHelpers} from 'formik';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {useConfirm, useMessage} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage, ResourceBlock} from '~/components/ResourcePage';
import {useAbility} from '~/lib/ability';
import {Copy} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {BusinessIncentivesAbility} from '~/modules/BusinessIncentives/ability';
import {fetchBusinessIncentive, deleteBusinessIncentive, updateBusinessIncentive} from '~/modules/BusinessIncentives/api';
import {
    BusinessIncentiveForm,
    IBusinessIncentiveFormProps,
    IBusinessIncentiveFormValues
} from '~/modules/BusinessIncentives/forms/BusinessIncentiveForm';
import {businessIncentivesLinks} from '~/modules/BusinessIncentives/links';
import {
    IBusinessIncentive,
    IBusinessIncentiveUpdateRequest,
    IncentiveState,
    incentiveStateLabel,
    incentiveStructureLabel,
    incentiveTypeLabel,
    structureRequiresTier,
    structureRequiresValue,
} from '~/modules/BusinessIncentives/models/BusinessIncentive';
import {BUSINESS_INCENTIVES_QUERY_KEY} from '~/modules/BusinessIncentives/queries';
import {hasStartedPeriod} from '~/modules/BusinessIncentives/utils/period';
import {toDisplayNumber, toDisplayText} from '~/modules/BusinessIncentives/utils/value';
import style from '~/modules/BusinessIncentives/views/BusinessIncentiveView.module.scss';

interface IBusinessIncentiveViewParams {
    incentiveId: string;
}

const BUSINESS_INCENTIVE_QUERY_KEY = (id: number) => ['business-incentive', id] as const;

const getErrorMessage = (error: unknown): string => {
    const value = error as {
        response?: {data?: unknown};
        message?: string;
    };
    const responseData = value?.response?.data;
    if (typeof responseData === 'string') {
        return responseData;
    }
    if (responseData && typeof responseData === 'object' && 'message' in responseData) {
        const responseMessage = (responseData as {message?: unknown}).message;
        if (typeof responseMessage === 'string' && responseMessage.trim()) {
            return responseMessage;
        }
    }
    return value?.message ?? 'Unknown error';
};

const formatScopePreview = (scope: IBusinessIncentive['scope']): string => toDisplayText(scope);

const formatTierUsd = (value: number): string => `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
}).format(value)}`;

const formatTierPercent = (value: number): string => `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
}).format(value)}%`;

const formatExpenseValue = (incentive: IBusinessIncentive): string => {
    if (typeof incentive.value !== 'number' || !Number.isFinite(incentive.value)) {
        return '—';
    }
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(incentive.value);
    if (incentive.valueUnit === 'PERCENT') {
        return `${formatted}%`;
    }
    return `$${formatted}`;
};

const formatTierName = (value: string): string => {
    const normalized = value.trim();
    if (!normalized) {
        return '-';
    }
    if (normalized === 'not_applicable') {
        return 'Not applicable';
    }
    if (normalized === 'miss') {
        return 'Miss';
    }
    if (/^tier_\d+$/i.test(normalized)) {
        return `Tier ${normalized.split('_')[1]}`;
    }
    return normalized;
};

const toUpdatePayload = (values: IBusinessIncentiveFormValues): IBusinessIncentiveUpdateRequest => {
    const isTierStructure = structureRequiresTier(values.structure);
    const requiresValue = structureRequiresValue(values.structure);
    const monetaryLocked = hasStartedPeriod(values.startDate);
    const tiers = isTierStructure
        ? values.tiers.map((tier, index) => ({
            name: tier.name.trim(),
            thresholdUsd: index === 0 ? 0 : (Number(tier.thresholdUsd) || 0),
            percentage: Number(tier.percentage) || 0,
        }))
        : undefined;

    const normalizedCurrentTier = isTierStructure
        ? (values.currentTier.trim() || null)
        : undefined;

    return {
        name: values.name.trim(),
        description: values.description.trim(),
        scope: values.scope.trim(),
        state: values.state,
        currentTier: normalizedCurrentTier,
        tiers,
        value: requiresValue && !monetaryLocked
            ? (values.value === '' ? null : Number(values.value))
            : undefined,
        endDate: values.endDate || undefined,
    };
};

export const BusinessIncentiveView = () => {
    const ability = useAbility(BusinessIncentivesAbility);
    const modals = useModals();
    const confirm = useConfirm();
    const message = useMessage();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const params = useParams<IBusinessIncentiveViewParams>();

    const incentiveId = Number(params.incentiveId);
    const hasValidId = Number.isFinite(incentiveId) && incentiveId > 0;

    const incentiveQuery = useQuery({
        queryKey: hasValidId ? BUSINESS_INCENTIVE_QUERY_KEY(incentiveId) : ['business-incentive', 'invalid'],
        queryFn: () => fetchBusinessIncentive(incentiveId),
        enabled: hasValidId,
    });

    const updateMutation = useMutation({
        mutationFn: ({id, payload}: {id: number; payload: IBusinessIncentiveUpdateRequest}) =>
            updateBusinessIncentive(id, payload),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteBusinessIncentive(id),
    });

    const invalidateAll = async () => {
        await queryClient.invalidateQueries({queryKey: BUSINESS_INCENTIVES_QUERY_KEY});
        if (hasValidId) {
            await queryClient.invalidateQueries({queryKey: BUSINESS_INCENTIVE_QUERY_KEY(incentiveId)});
        }
    };

    const formSubmit = (
        values: IBusinessIncentiveFormValues,
        formikActions: FormikHelpers<IBusinessIncentiveFormValues>,
        close: () => void
    ) => {
        const id = toDisplayNumber(values.id);
        if (id === null) {
            return;
        }

        updateMutation.mutateAsync({
            id,
            payload: toUpdatePayload(values),
        })
            .then(async updated => {
                queryClient.setQueryData(BUSINESS_INCENTIVE_QUERY_KEY(id), updated);
                await invalidateAll();
                message('Expense saved successfully.', {
                    intent: Intent.SUCCESS,
                    icon: <i className='fa fa-check'/>,
                });
                close();
            })
            .catch(onSubmitError(formikActions));
    };

    const editIncentiveModal = modals.create<IBusinessIncentiveFormProps>(BusinessIncentiveForm, {
        onSubmit: formSubmit,
    });

    const handleDelete = (incentive: IBusinessIncentive) => {
        const id = toDisplayNumber(incentive.id);
        if (id === null) {
            return;
        }
        confirm(`Delete expense "${toDisplayText(incentive.name)}"?`, {
            intent: Intent.DANGER,
            icon: <i className='fa fa-trash'/>,
            onOk: () => {
                deleteMutation.mutate(id, {
                    onSuccess: async () => {
                        await invalidateAll();
                        message('Expense deleted.', {
                            intent: Intent.SUCCESS,
                            icon: <i className='fa fa-check'/>,
                        });
                        navigate(businessIncentivesLinks.businessIncentives());
                    },
                    onError: error => {
                        message(getErrorMessage(error), {
                            intent: Intent.DANGER,
                            icon: <i className='fa fa-times'/>,
                        });
                    }
                });
            },
        });
    };

    if (!hasValidId) {
        return (
            <Breadcrumb breadcrumb={{
                to: businessIncentivesLinks.businessIncentives(),
                title: 'Expenses'
            }}
            >
                <BreadcrumbsResourcePage title='Expense not found'>
                    <Button onClick={() => navigate(businessIncentivesLinks.businessIncentives())}>
                        Back to expenses
                    </Button>
                </BreadcrumbsResourcePage>
            </Breadcrumb>
        );
    }

    if (incentiveQuery.isPending) {
        return <Progress message='Loading expense'/>;
    }

    if (!incentiveQuery.data) {
        return (
            <Breadcrumb breadcrumb={{
                to: businessIncentivesLinks.businessIncentives(),
                title: 'Expenses'
            }}
            >
                <BreadcrumbsResourcePage title='Expense not found'>
                    <Button onClick={() => navigate(businessIncentivesLinks.businessIncentives())}>
                        Back to expenses
                    </Button>
                </BreadcrumbsResourcePage>
            </Breadcrumb>
        );
    }

    const incentive = incentiveQuery.data;
    const isBusy =
        incentiveQuery.isFetching ||
        updateMutation.isPending ||
        deleteMutation.isPending;

    const currentTier = toDisplayText(incentive.currentTier, '').trim();
    const scopePreview = formatScopePreview(incentive.scope);

    return (
        <Breadcrumb breadcrumb={{
            to: businessIncentivesLinks.businessIncentive(incentive.id ?? incentiveId),
            title: toDisplayText(incentive.name, 'Expense')
        }}
        >
            <BreadcrumbsResourcePage
                title={toDisplayText(incentive.name, 'Expense')}
                toolbar={
                    <>
                        {ability.canEdit() && (
                            <Button
                                intent={Intent.PRIMARY}
                                leftIcon={<i className='fa fa-edit'/>}
                                disabled={isBusy || toDisplayText(incentive.state, '') === IncentiveState.CLOSED}
                                onClick={() => editIncentiveModal.show({incentive})}
                                responsive
                            >
                                Edit
                            </Button>
                        )}
                        {ability.canDelete() && (
                            <Button
                                intent={Intent.DANGER}
                                leftIcon={<i className='fa fa-trash'/>}
                                disabled={isBusy}
                                onClick={() => handleDelete(incentive)}
                                responsive
                            >
                                Delete
                            </Button>
                        )}
                    </>
                }
            >
                <div className={style.topBlocks}>
                    <ResourceBlock title='General'>
                        <div className={style.generalGrid}>
                            <div className={style.generalColumn}>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>ID</div>
                                    <div className={style.infoValue}>{toDisplayText(incentive.id)}</div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Title</div>
                                    <div className={style.infoValue}>{toDisplayText(incentive.name)}</div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Type</div>
                                    <div className={style.infoValue}>
                                        {incentiveTypeLabel[incentive.incentiveType] ?? toDisplayText(incentive.incentiveType)}
                                    </div>
                                </div>
                            </div>
                            <div className={style.generalColumn}>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Structure</div>
                                    <div className={style.infoValue}>
                                        {incentiveStructureLabel[incentive.structure] ?? toDisplayText(incentive.structure)}
                                    </div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Value</div>
                                    <div className={style.infoValue}>{formatExpenseValue(incentive)}</div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Period</div>
                                    <div className={style.infoValue}>
                                        {`${toDisplayText(incentive.startDate)} → ${toDisplayText(incentive.endDate)}`}
                                    </div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>State</div>
                                    <div className={style.infoValue}>
                                        {incentiveStateLabel[incentive.state] ?? toDisplayText(incentive.state)}
                                    </div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Current tier</div>
                                    <div className={style.infoValue}>{currentTier ? formatTierName(currentTier) : '—'}</div>
                                </div>
                                <div className={style.infoItem}>
                                    <div className={style.infoLabel}>Description</div>
                                    <div className={style.infoValue}>{toDisplayText(incentive.description, '—')}</div>
                                </div>
                            </div>
                        </div>
                    </ResourceBlock>
                    <ResourceBlock title='Tiers' className={style.tiersBlock}>
                        {structureRequiresTier(incentive.structure) && incentive.tiers.length > 0 ? (
                            <div className={style.tierList}>
                                {incentive.tiers.map((tier, index) => {
                                    const tierName = tier.name.trim();
                                    const isActive = currentTier !== ''
                                        ? tierName === currentTier
                                        : index === 0;
                                    return (
                                        <div
                                            key={`${tierName || 'tier'}-${index}`}
                                            className={[style.tierRow, isActive ? style.tierRowActive : ''].join(' ')}
                                        >
                                            <div className={style.tierName}>{formatTierName(tier.name)}</div>
                                            <div className={style.tierMetric}>{formatTierUsd(tier.thresholdUsd)}</div>
                                            <div className={style.tierMetric}>{formatTierPercent(tier.percentage)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={style.emptyValue}>—</div>
                        )}
                    </ResourceBlock>
                </div>
                <ResourceBlock
                    title='Scope'
                    className='layout-content-box'
                    toolbar={<Copy text={scopePreview}/>}
                >
                    <pre className={style.scopeValue}>{scopePreview}</pre>
                </ResourceBlock>
            </BreadcrumbsResourcePage>
        </Breadcrumb>
    );
};
